import { formatBytes, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { MeterRow } from '@/types/platform';

function amount(value: number, unit: MeterRow['unit']): string {
  return unit === 'bytes' ? formatBytes(value) : formatNumber(value);
}

// One line of plan usage: what was used, the limit, and a bar when there is a limit.
export default function MeterBar({ row }: { row: MeterRow }) {
  const hasLimit = row.limit !== null;
  const ratio = hasLimit && row.limit ? Math.min(row.used / row.limit, 1) : 0;
  const over = hasLimit && row.limit !== null && row.used > row.limit;
  const near = hasLimit && ratio >= 0.8 && !over;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span>{row.label}</span>
        <span className="text-muted-foreground tabular-nums">
          {amount(row.used, row.unit)}
          {hasLimit ? ` of ${amount(row.limit ?? 0, row.unit)}` : ' · no cap'}
        </span>
      </div>
      {hasLimit ? (
        <div
          className="bg-muted mt-1.5 h-1.5 overflow-hidden rounded-full"
          role="progressbar"
          aria-label={row.label}
          aria-valuemin={0}
          aria-valuemax={row.limit ?? 0}
          aria-valuenow={row.used}
        >
          <div
            className={cn(
              'h-full rounded-full',
              over ? 'bg-red-600' : near ? 'bg-yellow-500' : 'bg-foreground/70'
            )}
            style={{ width: `${Math.max(ratio * 100, row.used > 0 ? 2 : 0)}%` }}
          />
        </div>
      ) : null}
      {over ? <p className="mt-1 text-xs text-red-600">Over the plan limit.</p> : null}
    </div>
  );
}
